import Shell from '../components/Shell';
import Seo from '../components/Seo';
import { PageHero, Section } from '../components/Pieces';

function H2({ children }) {
  return <h2 className="font-display text-2xl md:text-3xl font-bold text-[var(--mm-ink)] mt-12 first:mt-0">{children}</h2>;
}

function P({ children }) {
  return <p className="mt-4 text-[var(--mm-ink)]/75 leading-relaxed">{children}</p>;
}

function Ul({ children }) {
  return <ul className="mt-3 space-y-1.5 list-disc pl-5 text-[var(--mm-ink)]/75 leading-relaxed">{children}</ul>;
}

export default function PrivacyPolicy() {
  return (
    <Shell>
      <Seo
        title="Privacy Policy"
        path="/privacy-policy"
        description="Privacy Policy for the Mashmari Advanced LMS App. How Mashmari collects, uses, shares and protects personal information, and how to exercise your data rights."
      />
      <PageHero
        kicker="Legal"
        title="Privacy Policy"
        sub="Privacy Policy for the Mashmari Advanced LMS App. Effective 24 October 2024. Last updated 24 October 2024."
        breadcrumbs={[{ label: 'Privacy Policy' }]}
      />
      <Section bg="white">
        <article className="max-w-3xl">
          <P>At Mashmari, we value your privacy and are committed to protecting your personal information. This Privacy Policy outlines the data we collect, how we use it, and the steps we take to ensure your privacy when you use the Mashmari Advanced LMS App (“the App”). By using the App, you agree to the collection and use of information in accordance with this Privacy Policy.</P>
          <P>This Privacy Policy reflects our commitment to transparency and compliance with privacy laws, including the Digital Personal Data Protection Act (DPDPA) 2023, General Data Protection Regulation (GDPR) and Information Technology (Reasonable Security Practices and Procedures and Sensitive Personal Data or Information) Rules, 2011.</P>
          <P>If you have any comments or suggestions regarding the policy, please write to <a className="text-[var(--mm-purple)] hover:underline" href="mailto:dataprotection@mashmari.in">dataprotection@mashmari.in</a>.</P>

          <H2>1. User Duties</H2>
          <P>As a user of the Mashmari Advanced LMS App (“App”), you are expected to comply with the following responsibilities in accordance with applicable laws, including the Digital Personal Data Protection Act, 2023:</P>
          <div className="mt-4 space-y-4 text-[var(--mm-ink)]/75 leading-relaxed">
            <div>
              <p className="font-semibold text-[var(--mm-ink)]">a) Provide Accurate Information:</p>
              <Ul>
                <li>Ensure that all personal data (such as your name, email, mobile number, etc.) provided during registration or use of the App is accurate and up-to-date.</li>
                <li>Do not submit false or misleading information.</li>
              </Ul>
            </div>
            <div>
              <p className="font-semibold text-[var(--mm-ink)]">b) Respect the Privacy of Others:</p>
              <Ul>
                <li>Do not collect, share, or misuse the personal data of other users or any information obtained through the App.</li>
                <li>Avoid any unauthorized access or attempt to manipulate data belonging to others.</li>
              </Ul>
            </div>
            <div>
              <p className="font-semibold text-[var(--mm-ink)]">c) Maintain Confidentiality of Credentials:</p>
              <Ul>
                <li>Safeguard your login credentials (username, password, and other security tokens) and avoid sharing them with others.</li>
                <li>Notify us immediately of any unauthorized use of your account or security breach.</li>
              </Ul>
            </div>
            <div>
              <p className="font-semibold text-[var(--mm-ink)]">d) Do Not Impersonate or Falsify Identity:</p>
              <Ul>
                <li>Refrain from impersonating another person, organization, or entity while using the App.</li>
                <li>Do not create fake profiles or engage in any activity that could mislead others.</li>
              </Ul>
            </div>
            <div>
              <p className="font-semibold text-[var(--mm-ink)]">e) Compliance with Consent Mechanisms:</p>
              <Ul>
                <li>Honor the privacy choices of other users, including opting in or out of specific features or data processing activities.</li>
                <li>Avoid any attempt to override or manipulate another user’s data privacy settings.</li>
              </Ul>
            </div>
            <div>
              <p className="font-semibold text-[var(--mm-ink)]">f) Responsible Use of the App:</p>
              <Ul>
                <li>Use the App only for lawful purposes and in accordance with the terms and conditions.</li>
                <li>Refrain from activities that could harm the App’s operations, infrastructure, or other users, such as hacking or introducing malicious software.</li>
              </Ul>
            </div>
            <div>
              <p className="font-semibold text-[var(--mm-ink)]">g) Grievance Reporting:</p>
              <Ul>
                <li>If you encounter any issues regarding your personal data, promptly use the provided grievance redressal mechanisms to report and resolve the issue.</li>
                <li>Engage constructively with the grievance redressal process and avoid filing frivolous complaints.</li>
              </Ul>
            </div>
            <div>
              <p className="font-semibold text-[var(--mm-ink)]">h) Compliance with Legal Obligations:</p>
              <Ul>
                <li>Adhere to all relevant laws, including the DPDPA 2023, while using the App.</li>
                <li>Cooperate with lawful requests from the App administrators in case of investigations related to data breaches or misuse.</li>
              </Ul>
            </div>
          </div>
          <P>Failure to adhere to these duties may result in invalidating the benefits of this policy, restricted access to the App or other appropriate actions, including penalties, in accordance with applicable laws and the terms of service.</P>

          <H2>2. Information We Collect</H2>
          <P>We collect and process the following types of Personally Identifiable Information (PII) and non-PII:</P>
          <div className="mt-4 space-y-4 text-[var(--mm-ink)]/75 leading-relaxed">
            <div>
              <p className="font-semibold text-[var(--mm-ink)]">Personal Information:</p>
              <Ul>
                <li>Name</li>
                <li>Mobile number</li>
                <li>Email address</li>
              </Ul>
            </div>
            <div>
              <p className="font-semibold text-[var(--mm-ink)]">Learning Activity &amp; Performance Data:</p>
              <Ul>
                <li>Courses accessed and progress</li>
                <li>Quizzes, assessments, and performance scores</li>
                <li>Certificates and achievements</li>
              </Ul>
            </div>
            <div>
              <p className="font-semibold text-[var(--mm-ink)]">Device and Usage Information:</p>
              <Ul>
                <li>Device type, OS, and browser information</li>
                <li>IP address and location (if enabled)</li>
                <li>Crash reports and diagnostic data</li>
              </Ul>
            </div>
          </div>

          <H2>3. How We Use Your Information</H2>
          <P>The information collected is used to:</P>
          <Ul>
            <li>Provide, maintain, and improve the App’s services.</li>
            <li>Personalize learning experiences and recommend relevant content.</li>
            <li>Manage course registrations, progress tracking, and certification.</li>
            <li>Send notifications about course updates, achievements, and reminders.</li>
            <li>Respond to user inquiries, requests, or technical support needs.</li>
            <li>Comply with legal obligations and resolve disputes, if any.</li>
          </Ul>

          <H2>4. Data Sharing and Disclosure</H2>
          <P>We do not sell your personal information to third parties. However, your data may be shared in the following cases:</P>
          <Ul>
            <li><span className="font-semibold text-[var(--mm-ink)]">With Educational Institutions:</span> If you are enrolled through an institution, they may receive performance data to monitor your progress.</li>
            <li><span className="font-semibold text-[var(--mm-ink)]">With Service Providers:</span> We may engage third-party providers for analytics, cloud storage, and communication services. These providers are contractually bound to protect your data.</li>
            <li><span className="font-semibold text-[var(--mm-ink)]">Legal Requirements:</span> If required by law or in response to legal requests, we may disclose your information to relevant authorities.</li>
          </Ul>

          <H2>5. Data Retention and Deletion</H2>
          <P>We retain your data only as long as necessary for the purposes outlined in this policy. Once the data is no longer needed, it will be securely deleted or anonymized.</P>
          <P>Users may request the deletion of their data by contacting us at <a className="text-[var(--mm-purple)] hover:underline" href="mailto:dataprotection@mashmari.in">dataprotection@mashmari.in</a>.</P>

          <H2>6. Your Data Protection Rights</H2>
          <P>Where you have a paid subscription, you have the right to:</P>
          <Ul>
            <li><span className="font-semibold text-[var(--mm-ink)]">Access:</span> Request a copy of your personal data.</li>
            <li><span className="font-semibold text-[var(--mm-ink)]">Correction:</span> Request corrections to inaccurate or incomplete information.</li>
            <li><span className="font-semibold text-[var(--mm-ink)]">Deletion:</span> Request the deletion of your data, subject to legal and operational obligations.</li>
            <li><span className="font-semibold text-[var(--mm-ink)]">Withdrawal of Consent:</span> If you have provided consent for data collection, you can withdraw it at any time.</li>
          </Ul>
          <P>Remember, without a paid subscription, you can still request your data to be deleted.</P>
          <P>Please contact us at <a className="text-[var(--mm-purple)] hover:underline" href="mailto:dataprotection@mashmari.in">dataprotection@mashmari.in</a> for any of these requests, as described in the clause 4 above.</P>

          <H2>7. Security Measures</H2>
          <P>We employ industry-standard security practices to protect your data from unauthorized access, alteration, disclosure, or destruction. This includes encryption, secure access controls, and regular security audits. However, no system can be completely secure, and we cannot guarantee the absolute security of your data.</P>

          <H2>8. Use of Cookies and Analytics</H2>
          <P>The App may use cookies or similar tracking technologies to enhance your experience. Analytics services may collect usage statistics to help us understand how users engage with the App.</P>

          <H2>9. Children’s Privacy</H2>
          <P>Our App is intended for educational purposes. If the user is under the age of 13, parental consent is required before the collection and use of personal data.</P>

          <H2>10. Changes to this Privacy Policy</H2>
          <P>We may update this Privacy Policy from time to time. Changes will be posted on this page, and we will notify you via the App or email if the changes are significant. Please review the policy periodically.</P>

          <H2>11. Compliance to the DPDPA 2023</H2>
          <P>The Mashmari ALMS App endeavors to comply to the evolving provisions of the Digital Personal Data Protection Act (DPDPA) 2023.</P>
          <div className="mt-4 space-y-4 text-[var(--mm-ink)]/75 leading-relaxed">
            <p><span className="font-semibold text-[var(--mm-ink)]">a) Consent Management:</span> We ensure that user consent is explicit, informed, and recorded, especially for collecting personal data such as names, mobile numbers, and learning data. Users must also have the right to withdraw consent at any time.</p>
            <p><span className="font-semibold text-[var(--mm-ink)]">b) Right to Access and Erasure:</span> The policy explicitly mentions users’ rights to access their data, seek corrections, and request erasure unless retention is required for legal purposes.</p>
            <p><span className="font-semibold text-[var(--mm-ink)]">c) Grievance Redressal Mechanism:</span> Mention a clear mechanism for users to lodge grievances related to data privacy, and ensure timely response to complaints, as mandated under DPDPA.</p>
            <p><span className="font-semibold text-[var(--mm-ink)]">d) Parental Consent for Minors:</span> Parental or guardian consent is collected before processing children’s data, and restrict tracking or behavioral profiling targeted at children.</p>
            <p><span className="font-semibold text-[var(--mm-ink)]">e) Cross-Border Data Transfers:</span> Personal data will not be transferred to restricted jurisdictions as defined by the Indian government, and users are informed of any data transfer outside India.</p>
            <p><span className="font-semibold text-[var(--mm-ink)]">f) Security Measures:</span> We implement “reasonable security safeguards” to protect user data, as required by the Act.</p>
            <p><span className="font-semibold text-[var(--mm-ink)]">g) Data Breach Reporting:</span> We notify both the Data Protection Board of India and affected users in the event of a data breach.</p>
            <p><span className="font-semibold text-[var(--mm-ink)]">h) Exemptions and Obligations:</span> If any exemptions apply to (e.g., for research purposes), these shall be explicitly mentioned.</p>
            <p><span className="font-semibold text-[var(--mm-ink)]">i) Penalties and Compliance:</span> We acknowledge that willful and deliberate non-compliance with data protection provisions attracts penalties, in alignment with the framework under the DPDPA.</p>
          </div>

          <H2>12. Contact Us</H2>
          <P>If you have any questions or concerns about this Privacy Policy or how we handle your data, please contact us:</P>
          <P>
            Mashmari Megh Advanced LMS<br />
            Website: <a className="text-[var(--mm-purple)] hover:underline" href="https://mashmari.in">https://mashmari.in</a><br />
            Email: <a className="text-[var(--mm-purple)] hover:underline" href="mailto:dataprotection@mashmari.in">dataprotection@mashmari.in</a>
          </P>
        </article>
      </Section>
    </Shell>
  );
}
